<template>
  <div class="auth-container">
    <div class="auth-card-wrapper">
      <themed-card>
        <div class="auth-form">
          <!-- Logo -->
          <div class="auth-logo">
            <img :src="logoUrl" alt="REoptimizer Logo">
          </div>

          <!-- General Errors (not field-specific) -->
          <div v-if="generalErrors.length > 0" class="auth-alert auth-alert--error">
            <theme-icon icon="alert-circle" type="icons" class="auth-alert__icon" />
            <div class="auth-alert__content">
              <p v-for="(error, index) in generalErrors" :key="index">{{ error }}</p>
            </div>
          </div>

          <!-- Form -->
          <themed-form
            :fields="formFields"
            :model-value="formData"
            :errors="fieldErrors"
            layout="vertical"
            @input="handleInput"
            @submit="handleSubmit"
          >
            <template #before-fields>
              <input type="hidden" name="_token" :value="csrfToken">
            </template>

            <template #submit>
              <div class="auth-actions">
                <themed-button type="submit" variant="default">
                  Register
                </themed-button>
                <a :href="loginRoute" class="auth-actions__link">
                  Back to Login
                </a>
              </div>
            </template>
          </themed-form>
        </div>
      </themed-card>

      <!-- Copyright -->
      <p class="auth-copyright">
        &copy;{{ currentYear }} REoptimizer&reg;, LLC. All rights reserved.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegisterForm',
  props: {
    errors: {
      type: [Array, Object],
      default: () => ({})
    },
    oldName: {
      type: String,
      default: ''
    },
    oldEmail: {
      type: String,
      default: ''
    },
    oldPhone: {
      type: String,
      default: ''
    },
    oldExtension: {
      type: String,
      default: ''
    },
    registerRoute: {
      type: String,
      required: true
    },
    loginRoute: {
      type: String,
      required: true
    },
    csrfToken: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      formData: {
        name: this.oldName,
        email: this.oldEmail,
        phone: this.oldPhone,
        extension: this.oldExtension,
        password: '',
        password_confirmation: ''
      }
    };
  },
  computed: {
    formFields() {
      return [
        {
          name: 'name',
          type: 'input',
          inputType: 'text',
          label: 'Name',
          required: true,
          componentProps: {
            autofocus: true
          }
        },
        {
          name: 'email',
          type: 'input',
          inputType: 'email',
          label: 'Email',
          required: true
        },
        {
          name: 'phone',
          type: 'input',
          inputType: 'tel',
          label: 'Phone Number',
          required: true,
          componentProps: {
            placeholder: '(555) 123-4567'
          }
        },
        {
          name: 'extension',
          type: 'input',
          inputType: 'text',
          label: 'Extension',
          required: true,
          componentProps: {
            placeholder: 'e.g., 1234',
            maxlength: 5
          }
        },
        {
          name: 'password',
          type: 'input',
          inputType: 'password',
          label: 'Password',
          required: true
        },
        {
          name: 'password_confirmation',
          type: 'input',
          inputType: 'password',
          label: 'Confirm Password',
          required: true
        }
      ];
    },
    currentYear() {
      return new Date().getFullYear();
    },
    logoUrl() {
      return '/img/logos/reoptimizer-small.png';
    },
    // Convert Laravel errors to field-specific format
    fieldErrors() {
      // If errors is already an object (field-specific), return it
      if (typeof this.errors === 'object' && !Array.isArray(this.errors)) {
        return this.errors;
      }
      // If errors is an array (general errors), return empty object
      return {};
    },
    // General errors (not field-specific)
    generalErrors() {
      if (Array.isArray(this.errors)) {
        return this.errors;
      }
      return [];
    }
  },
  methods: {
    handleInput(data) {
      this.formData = data;
    },
    async handleSubmit() {
      // Create a form and submit it
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = this.registerRoute;
      
      // Add CSRF token
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = '_token';
      csrfInput.value = this.csrfToken;
      form.appendChild(csrfInput);
      
      // Add form data
      Object.keys(this.formData).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = this.formData[key];
        form.appendChild(input);
      });
      
      document.body.appendChild(form);
      form.submit();
    }
  }
}
</script>
